import type { MouseEvent, ReactNode } from 'react';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { NavArrowRight, Plus } from 'iconoir-react';
import { Loader2 } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from 'components/ui/collapsible';
import { TooltipProvider } from 'components/ui/tooltip';
import { SidebarTreeItem } from 'components/blocks/layout/sidebar-items';
import { sidebarSectionItemRowClassName } from 'components/blocks/layout/sidebar-nav-item-styles';
import { SidebarNavTooltip } from 'components/blocks/layout/sidebar-nav-tooltip';
import { useSidebar } from 'components/ui/sidebar';
import { useTheme } from 'components/core/theme-provider';
import { cn } from 'lib/utils';
import { getConversationDisplayTitle } from 'utils/chat-utilities';
import { SIDEBAR_NAV_ICON_SIZE } from 'components/blocks/layout/sidebar-nav-icon';
import recentChatLogo from 'assets/images/recent-chat-logo.svg';
import { getOnboardingRecentChatPath } from 'features/onboarding/onboarding-journey';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { useOnboardingStore } from 'state/store/onboarding/use-onboarding-store';
import { useChatStore } from 'state/store/chat/use-chat-store';
import { useAgentSelectionStore } from 'state/store/chat/use-agent-selection-store';

type BuddyChatItem = {
  ConversationId: string;
  ConversationTitle?: string;
  IsGroup?: boolean;
  ProjectId?: string | null;
  AgentId?: string | null;
  agentIds?: string[] | null;
};

type BuddyChatsSectionProps = {
  conversations: BuddyChatItem[];
  isSidebarLoading: boolean;
  renderRightAction: (id: string, title: string) => ReactNode;
};

const MAX_VISIBLE_BUDDY_ROWS = 3;

/** Plain chats for the Buddy menu: no agent and no project. */
export function isBuddySidebarChat(item: BuddyChatItem) {
  if (item.IsGroup) return false;
  if (item.ProjectId) return false;
  if (item.AgentId) return false;
  if (item.agentIds && item.agentIds.length > 0) return false;
  return true;
}

function onboardingConversationId(path: string | null) {
  const match = path?.match(/\/chat\/([^/?]+)/);
  return match?.[1];
}

export function BuddyChatsSection({
  conversations,
  isSidebarLoading,
  renderRightAction,
}: BuddyChatsSectionProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { isMobile, setOpenMobile } = useSidebar();
  const [isOpen, setIsOpen] = useState(true);
  const [showAllChats, setShowAllChats] = useState(false);
  const userId = useCommonStore((state) => state.user?.id);
  const setNewChat = useChatStore((state) => state.setNewChat);
  const disableNewChat = useChatStore((state) => state.disableNewChat);
  const setIsIncognitoChat = useChatStore((state) => state.setIsIncognitoChat);
  const clearUserSelection = useAgentSelectionStore((state) => state.clearUserSelection);
  const onboardingProgress = useOnboardingStore((state) =>
    userId ? state.progressByUser[userId] : undefined
  );
  const onboardingPath = getOnboardingRecentChatPath(onboardingProgress);
  const onboardingChatId = onboardingConversationId(onboardingPath);
  const onboardingChatPath = onboardingPath?.split('?')[0] ?? '';
  const titleColor = theme === 'dark' ? 'text-grey-300' : 'text-foreground';
  const newChatLabel = t('CHAT_WITH_AGENT', {
    agent: t('BUDDY', { defaultValue: 'Buddy' }),
    defaultValue: 'Chat with {{agent}}',
  });

  const buddyChats = (conversations ?? []).filter(
    (item) => isBuddySidebarChat(item) && item.ConversationId !== onboardingChatId
  );
  const rowCount = buddyChats.length + (onboardingPath ? 1 : 0);
  const hiddenCount = Math.max(rowCount - MAX_VISIBLE_BUDDY_ROWS, 0);
  const visibleChats = showAllChats
    ? buddyChats
    : buddyChats.slice(0, onboardingPath ? MAX_VISIBLE_BUDDY_ROWS - 1 : MAX_VISIBLE_BUDDY_ROWS);
  const listIsScrollable = showAllChats && hiddenCount > 0;

  const openChat = (to: string) => {
    if (isMobile) setOpenMobile(false);
    navigate(to);
  };

  const createNewChat = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (disableNewChat) return;
    if (isMobile) setOpenMobile(false);
    setNewChat(true);
    setIsIncognitoChat(false);
    if (userId) clearUserSelection(userId);
    navigate({ pathname: '/chat', search: '' });
  };

  return (
    <TooltipProvider delayDuration={0}>
      <Collapsible open={isOpen} onOpenChange={setIsOpen} className="flex w-full flex-col gap-2">
        <div
          data-buddy-tour-target="choose-agent"
          className={cn(
            sidebarSectionItemRowClassName,
            'pr-0 hover:bg-transparent dark:hover:bg-transparent'
          )}
        >
          <CollapsibleTrigger asChild>
            <button type="button" className="flex min-w-0 flex-1 items-center gap-2">
              <img
                src={recentChatLogo}
                alt=""
                className="size-5 shrink-0"
                width={SIDEBAR_NAV_ICON_SIZE}
                height={SIDEBAR_NAV_ICON_SIZE}
              />
              <span
                className={cn(
                  'min-w-0 flex-1 truncate text-left font-sans text-sm font-medium leading-normal',
                  titleColor
                )}
              >
                {t('BUDDY', { defaultValue: 'Buddy' })}
              </span>
            </button>
          </CollapsibleTrigger>
          <div className="flex h-full shrink-0 items-center gap-1">
            <SidebarNavTooltip label={newChatLabel}>
              <button
                type="button"
                data-testid="buddy-new-chat"
                aria-label={newChatLabel}
                disabled={disableNewChat}
                onClick={createNewChat}
                className="flex h-full items-center text-grey-300 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Plus width={16} height={16} strokeWidth={1.875} />
              </button>
            </SidebarNavTooltip>
            <CollapsibleTrigger asChild>
              <button
                type="button"
                tabIndex={-1}
                aria-hidden="true"
                className="flex h-full items-center"
              >
                <NavArrowRight
                  width={16}
                  height={16}
                  strokeWidth={1.875}
                  className={cn(
                    'shrink-0 text-grey-300 transition-transform duration-200',
                    isOpen && 'rotate-90'
                  )}
                />
              </button>
            </CollapsibleTrigger>
          </div>
        </div>
        <CollapsibleContent className="flex flex-col">
          <div
            data-testid="buddy-chat-list"
            className={cn(
              listIsScrollable &&
                'max-h-48 overflow-y-auto [scrollbar-width:thin] [scrollbar-color:hsl(var(--grey-200))_transparent] [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-grey-200'
            )}
          >
            {onboardingPath && (
              <SidebarTreeItem
                label={t('ONBOARDING_AND_WELCOME', { defaultValue: 'Onboarding & Welcome' })}
                variant="section"
                className="py-3.5 pl-9"
                isActive={
                  onboardingPath === '/onboarding'
                    ? pathname === '/onboarding'
                    : pathname.startsWith(onboardingChatPath)
                }
                onClick={() => openChat(onboardingPath)}
              />
            )}
            {visibleChats.map((item) => {
              const displayTitle = getConversationDisplayTitle(item.ConversationTitle, t('NEW_CHAT'));
              return (
                <SidebarTreeItem
                  key={item.ConversationId}
                  label={displayTitle}
                  variant="section"
                  className="py-3.5 pl-9"
                  isActive={pathname.includes(item.ConversationId)}
                  onClick={() => openChat(`/chat/${item.ConversationId}`)}
                  rightAction={renderRightAction(item.ConversationId, displayTitle)}
                />
              );
            })}
            {isSidebarLoading && buddyChats.length === 0 && (
              <div className="flex h-10 items-center justify-center py-2">
                <Loader2 className="h-4 w-4 animate-spin text-grey-400" />
              </div>
            )}
            {!isSidebarLoading && buddyChats.length === 0 && !onboardingPath && (
              <div className="px-2 py-1 text-center">
                <p className="mb-2 text-xs italic text-muted-foreground">
                  {t('NO_CONVERSATIONS_YET') || 'No conversations yet'}
                </p>
              </div>
            )}
          </div>
          {hiddenCount > 0 && (
            <button
              type="button"
              onClick={() => setShowAllChats((open) => !open)}
              className={cn(
                sidebarSectionItemRowClassName,
                'pl-9 text-sm font-normal leading-heading text-grey-500 hover:bg-transparent dark:hover:bg-transparent'
              )}
            >
              {showAllChats
                ? t('SHOW_LESS', { defaultValue: 'Show less' })
                : `${t('SEE_MORE', { defaultValue: 'See more' })} (${hiddenCount})`}
            </button>
          )}
        </CollapsibleContent>
      </Collapsible>
    </TooltipProvider>
  );
}
